/**
 * Dense product table (CSS Grid, 4 columns) for the weekly proposal screen.
 * When isEditable=true, QuantitySelectors are live and call onQuantityChange.
 * Rows with qty > 0 get an olive left-border accent, so what is already in the
 * order can be picked out at a glance while scrolling a long catalogue.
 */
import React, {useState} from 'react'
import {MAX_ORDER_LINE_QUANTITY} from '@retail/orders-domain'
import {QuantitySelector, SearchInput, Tag} from '../../components'
import type {ProposalRow} from './proposalViewModel'
import styles from './ProductTable.module.scss'

type ProductTableProps = {
  rows: Array<ProposalRow>
  /** Quantities currently shown (may differ from server when editing). */
  localQuantities: Map<number, number>
  isEditable: boolean
  /** True while a save is on the wire — freezes the quantity controls. */
  isSaving?: boolean
  onQuantityChange: (productId: number, quantity: number) => void
  /** The store's per-line floor — the selector's min. */
  minQuantityPerProduct: number
}

export const ProductTable = ({
  rows,
  localQuantities,
  isEditable,
  isSaving = false,
  onQuantityChange,
  minQuantityPerProduct,
}: ProductTableProps): React.ReactElement => {
  const [search, setSearch] = useState('')

  const term = search.trim().toLowerCase()
  const filteredRows = term === ''
    ? rows
    : rows.filter((row) => row.product.name.toLowerCase().includes(term))

  // Live totals from local quantities — same formula as the week summary
  // ("N unidades en M referencias") so the two never read differently.
  const totalUnits = rows.reduce((sum, row) => sum + (localQuantities.get(row.product.id) ?? row.quantity), 0)
  const refsWithQty = rows.filter((row) => (localQuantities.get(row.product.id) ?? row.quantity) > 0).length

  return (
    <section className={`bg-white ${styles['product-table']}`} aria-label="Líneas del pedido">
      {/* Toolbar */}
      <div className={`row-flx flx-a-c flx-gap-12 ${styles['product-table__toolbar']}`}>
        <span className={`txt-bold-500 txt-kale ${styles['product-table__count']}`}>
          <strong>{totalUnits}</strong>
          {` ${totalUnits === 1 ? 'unidad' : 'unidades'} en ${refsWithQty} ${refsWithQty === 1 ? 'referencia' : 'referencias'}`}
        </span>
        <SearchInput
          className={`m-l-auto ${styles['product-table__search']}`}
          placeholder="Buscar plato…"
          value={search}
          onChange={setSearch}
        />
      </div>

      {/* Column headers */}
      <div className={`flx-a-c flx-gap-12 ${styles['product-table__thead']}`} role="row">
        <span>Plato</span>
        <span className={styles['product-table__col-hide-sm']}>Categoría</span>
        <span className={`txt-a-r ${styles['product-table__col-hide-sm']}`}>Peso</span>
        <span className="row-flx flx-j-c txt-a-c">Cantidad</span>
      </div>

      {/* Rows */}
      {filteredRows.length === 0 ? (
        <div className={`txt-a-c txt-light-kale ${styles['product-table__empty']}`}>
          No hay platos que coincidan con la búsqueda.
        </div>
      ) : (
        filteredRows.map((row) => (
          <ProductRow
            key={row.product.id}
            row={row}
            localQty={localQuantities.get(row.product.id) ?? row.quantity}
            isEditable={isEditable}
            isSaving={isSaving}
            onQuantityChange={onQuantityChange}
            minQuantityPerProduct={minQuantityPerProduct}
          />
        ))
      )}
    </section>
  )
}

type ProductRowProps = {
  row: ProposalRow
  localQty: number
  isEditable: boolean
  isSaving: boolean
  onQuantityChange: (productId: number, quantity: number) => void
  minQuantityPerProduct: number
}

// Memoised leaf: a quantity change rebuilds the localQuantities Map on every
// keystroke, re-rendering ProductTable — without this bailout every row would
// repaint even though only one row's props changed. Props are all stable or
// scalar (onQuantityChange is a stable useCallback, row comes from a useMemo),
// so the default shallow compare only re-renders the row whose localQty moved.
const ProductRow = React.memo(({row, localQty, isEditable, isSaving, onQuantityChange, minQuantityPerProduct}: ProductRowProps): React.ReactElement => {
  const hasQty = localQty > 0

  return (
    <div
      className={[
        'flx-a-c',
        'flx-gap-12',
        styles['product-table__row'],
        hasQty ? styles['product-table__row--has-qty'] : '',
      ].filter(Boolean).join(' ')}
      role="row"
    >
      {/* Product name */}
      <div className={`txt-bold-500 txt-kale ${styles['product-table__name']}`}>{row.product.name}</div>

      {/* Category — the kit's Tag; the name is resolved in the view-model */}
      <div className={styles['product-table__col-hide-sm']}>
        <Tag
          label={row.categoryName}
          bgColor="light-cream"
          borderRadius="s"
          className={styles['product-table__cat-tag']}
        />
      </div>

      {/* Weight */}
      <div
        className={[
          'txt-a-r',
          'txt-light-kale',
          styles['product-table__weight'],
          styles['product-table__col-hide-sm'],
        ].join(' ')}
      >
        {row.product.weight !== undefined ? `${row.product.weight} g` : '—'}
      </div>

      {/* Quantity — live when isEditable, disabled otherwise. Also frozen while
          a save is on the wire: an edit made in that window is not part of the
          payload already sent, and reconciling the reply would discard it. */}
      <div className="row-flx flx-j-c txt-a-c">
        <QuantitySelector
          value={localQty}
          onChange={(next) => onQuantityChange(row.product.id, next)}
          min={minQuantityPerProduct}
          max={MAX_ORDER_LINE_QUANTITY}
          disabled={!isEditable || isSaving}
        />
      </div>
    </div>
  )
})
ProductRow.displayName = 'ProductRow'
